import { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded'
import Logo from '../components/Logo'
import ProfileAvatar from '../components/ProfileAvatar'
import { AVATAR_PRESETS, presetAvatarUrl } from '../config/avatars'
import { useNavigate } from 'react-router-dom'
import { profileService } from '../services/profileService'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'
import { getActiveEventSlug } from '../services/eventService'

type AvatarKind = 'photo' | 'preset' | 'initials'

function apiMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data
    ?.detail
  return typeof detail === 'string' ? detail : fallback
}

/**
 * Mandatory first-login profile setup: photo/avatar + bio + headline (all
 * required). Saved to the cross-event master profile so returning attendees are
 * pre-filled at future events. Blocks the app until completed.
 */
export default function ProfileOnboardingPage() {
  const { me, refresh } = useAttendeeAuth()
  const navigate = useNavigate()
  const name = me?.fullName || me?.email || ''

  const [kind, setKind] = useState<AvatarKind>('preset')
  const [preset, setPreset] = useState(AVATAR_PRESETS[0].id)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [headline, setHeadline] = useState('')
  const [bio, setBio] = useState('')
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const avatarPreview = {
    avatarKind: kind,
    photoUrl,
    avatarPreset: preset,
  }

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const url = await profileService.uploadPhoto(file)
      setPhotoUrl(url)
      setKind('photo')
    } catch (err) {
      setError(apiMessage(err, 'Could not upload that image. Please try another.'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const canSubmit =
    headline.trim() &&
    bio.trim() &&
    (kind === 'initials' ||
      (kind === 'preset' && preset) ||
      (kind === 'photo' && photoUrl))

  const submit = async () => {
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      await profileService.complete({
        avatarKind: kind,
        photoUrl: kind === 'photo' ? photoUrl : null,
        avatarPreset: kind === 'preset' ? preset : null,
        headline: headline.trim(),
        bio: bio.trim(),
      })
      // Refresh the session so needsOnboarding clears, THEN navigate explicitly
      // into the event. Relying on the gate re-render alone left the attendee on
      // whatever route was active and, on any transient auth re-check, could
      // bounce them to the sign-in screen. An explicit navigate is deterministic.
      await refresh()
      const slug = me?.eventSlug || getActiveEventSlug()
      navigate(slug ? `/e/${slug}` : '/app', { replace: true })
    } catch (err) {
      setError(apiMessage(err, 'Could not save your profile. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 }, px: 2 }}>
      <Container maxWidth="sm" disableGutters>
        <Card variant="outlined" sx={{ borderRadius: 4, p: { xs: 3, md: 4 } }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Logo />
            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
              Complete your profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a photo (or pick an avatar), a short bio, and a headline. We'll
              remember this for future events so you only do it once.
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Avatar chooser */}
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                <ProfileAvatar avatar={avatarPreview} name={name} size={72} />
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PhotoCameraRoundedIcon />}
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                  >
                    {uploading ? 'Uploading…' : 'Upload photo'}
                  </Button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    hidden
                    onChange={onPickFile}
                  />
                </Box>
              </Stack>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={kind}
                onChange={(_, v) => v && setKind(v)}
                sx={{ mb: 1 }}
              >
                <ToggleButton value="photo" disabled={!photoUrl}>
                  Photo
                </ToggleButton>
                <ToggleButton value="preset">Avatar</ToggleButton>
                <ToggleButton value="initials">Initials</ToggleButton>
              </ToggleButtonGroup>

              {kind === 'preset' && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {AVATAR_PRESETS.map((p) => (
                    <Box
                      key={p.id}
                      component="img"
                      src={presetAvatarUrl(p.id)}
                      alt={p.label}
                      onClick={() => setPreset(p.id)}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        border: '3px solid',
                        borderColor: preset === p.id ? 'primary.main' : 'transparent',
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <TextField
              label="Headline"
              required
              fullWidth
              placeholder="e.g. VP Marketing at Acme"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
            />
            <TextField
              label="Bio"
              required
              fullWidth
              multiline
              minRows={3}
              placeholder="A sentence or two about you and what you do."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
            />

            <Button
              variant="contained"
              size="large"
              disabled={!canSubmit || saving}
              onClick={submit}
            >
              {saving ? 'Saving…' : 'Save and continue'}
            </Button>
          </Stack>
        </Card>
      </Container>
    </Box>
  )
}
